import {
	cache as cachePlugin,
	createClient,
	dedup as dedupPlugin,
	definePlugin,
	fetch as fetchPlugin,
	QueryCompositeOptions,
	useQuery
} from 'villus';
import { logError } from '@utils/error-tracking';
import { SANITY_PROJECT_ID } from '@constants/sanity';
import type { FetchOptions } from 'villus';

interface QueryWithContextOptions<TData, TVars = Record<string, unknown>>
	extends Omit<QueryCompositeOptions<TData, TVars>, 'tags'> {
	baseTag: string;
	contextTag?: string;
}

function getSanityGraphQLUrl(): string {
	const environment = import.meta.env.VITE_SANITY_ENV;
	const graphqlTag = import.meta.env.VITE_SANITY_GRAPHQL_TAG;

	return `https://${SANITY_PROJECT_ID}.apicdn.sanity.io/v2023-08-01/graphql/${environment}/${graphqlTag}`;
}

const errorHandlerPlugin = definePlugin(({ afterQuery }) => {
	afterQuery(({ error }) => {
		if (error) {
			logError(error, 'error', {
				key: 'GraphQL query error',
				data: {
					response: error?.response,
					message: error?.message,
					graphqlErrors: error?.graphqlErrors ?? []
				}
			});
			const accessDeniedGraphQL =
				error?.isGraphQLError &&
				error.graphqlErrors?.some((err) => err.message.toLowerCase().includes('access denied'));
			// Keeping this as a placeholder logic
			// Update the logic below to handle error cases better once we get more context around hidden fields
			if (accessDeniedGraphQL || error?.response?.status === 401) {
				window.location.href = '/login';
			}
		}
	});
});

export const villusClient = createClient({
	url: getSanityGraphQLUrl(),
	use: [
		function authPlugin({ opContext }: { opContext: FetchOptions }) {
			opContext.headers.Authorization = `Bearer ${import.meta.env.VITE_SANITY_TOKEN}`;
		},
		cachePlugin(),
		errorHandlerPlugin,
		dedupPlugin(),
		fetchPlugin()
	]
});

/**
 * Wrapper around useQuery that adds context tags to Villus queries.
 *
 * @param baseTag - The base tag for this query type (e.g., 'social_stats', 'annual_returns_table_data')
 * @param contextTag - Optional context string to track which component/page is making the request
 * @param options - Standard Villus query options (query, variables, etc.)
 *
 * @example
 * const { data } = await useQueryWithContext<ResponseType>({
 *   query: MyQuery,
 *   baseTag: 'social_stats',
 *   contextTag: 'homepage'
 * }).execute();
 */
export function useQueryWithContext<TData, TVars = Record<string, unknown>>({
	baseTag,
	contextTag,
	...options
}: QueryWithContextOptions<TData, TVars>) {
	return useQuery<TData, TVars>({
		client: villusClient,
		tags: [baseTag, contextTag].filter(Boolean) as Array<string>,
		...options
	});
}
